import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useShipmentTrackingContext } from "../context/ShipmentTrackingContext";
import { useAppState } from "../context/StateContext";
import { Filters, Searches, Sorts } from "../models/shipeezi.table";
import { Shipment } from "../models/shipments";
import { ShipmentsTracking } from "../models/shipments/shipments.tracking";
import ShipmentService from "../services/shipment.service";
import { ShipmentTrackingVersionService } from "../services/shipment.tracking.service";
import getRelatedFilters from "../util/getRelatedFilters";

interface SaveShipmentTrackingProps {
  id?: number;
  shipmentTracking: Shipment;
}

type Options = {
  filters?: Filters;
  searches?: Searches;
  sorting?: Sorts;
  page?: number;
  pageSize?: number;
};

interface GetShipmentsProps {
  options: Options;
  setShipments?: (shipments: ShipmentsTracking[]) => void;
  setTotal?: (total: number) => void;
}

interface ShareLinkProps {
  shipmentId: number;
  emails: string[];
}

const useActionShipmentTracking = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { currentUser } = useAppState();

  const { setShipmentTracking } = useShipmentTrackingContext();

  const getAllShipmentTracking = ({ options, setShipments }: GetShipmentsProps) => {
    const { searches, filters, sorting, page, pageSize } = options;

    return useQuery(
      ["shipments.list", { ...options }],
      () =>
        ShipmentTrackingVersionService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy: sorting ? JSON.stringify(sorting) : "",
          include: JSON.stringify([
            { association: "consignee" },
            {
              association: "communications",
              include: [
                { association: "customer" },
                { association: "user" },
                { association: "contact" },
              ],
            },
            { association: "customersToShipment", include: [{ association: "customer" }] },
            {
              association: "shipmentAgents",
              include: [{ association: "agent", include: [{ association: "contacts" }] }],
            },
            {
              association: "shipmentTracking",
              include: [{ association: "vesselPosition" }],
              where: Object.values(filters ?? {}).length
                ? getRelatedFilters(filters, "shipmentTracking")
                : null,
            },
            {
              association: "company",
              include: [{ association: "configs" }],
            },
          ]),
          searchByOr: searches && searches.length ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters, "shipment"),
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data && setShipments) setShipments(response.data);
        },
      }
    );
  };

  const getShipmentTrackingCount = ({ options, setTotal }: GetShipmentsProps) => {
    const { filters, searches } = options;

    return useQuery(
      ["shipment.list.count", filters, searches],
      () =>
        ShipmentTrackingVersionService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.id,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data && setTotal) setTotal(response.data);
        },
      }
    );
  };

  const getShipmentTrackingById = (shipmentTrackingId: number) => {
    return useQuery(
      ["shipment.get", shipmentTrackingId],
      () =>
        ShipmentService.getShipmentTrackingById(Number(shipmentTrackingId), {
          include: JSON.stringify([
            {
              association: "communications",
              include: [
                {
                  association: "user",
                },
                { association: "customer" },
                { association: "contact" },
              ],
            },
            {
              association: "shipmentTracking",
              include: [{ association: "vesselPosition" }],
            },
            {
              association: "shipmentAgents",
              include: [{ association: "agent", include: [{ association: "contacts" }] }],
            },
            {
              association: "customersToShipment",
              include: [{ association: "customer" }],
            },
            {
              association: "consignees",
            },
          ]),
        }),
      {
        enabled: !!shipmentTrackingId,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setShipmentTracking({
              ...response.data,
              customers: response.data.customersToShipment,
            });
          }
        },
      }
    );
  };

  const useUpdateShipmentTracking = () => {
    return useMutation(
      ["shipment.tracking.update"],
      ({ id, shipmentTracking }: SaveShipmentTrackingProps) => {
        // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
        const { products, ...shipmentTrackingToUpdate } = shipmentTracking;

        return ShipmentTrackingVersionService.update(Number(id), shipmentTrackingToUpdate);
      },
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          history.replace(PRIVATE_ROUTES.SHIPMENTS_TRACKING);
        },
        onError: (error: AxiosError<Error>) => {
          if (error && error.message) {
            NotificationToast.error(translate("anErrorOccuredTryAgain"));
          }
        },
      }
    );
  };

  const useCreateShipmentTracking = () => {
    return useMutation(
      ["shipment.tracking.create"],
      ({ shipmentTracking }: SaveShipmentTrackingProps) => {
        return ShipmentTrackingVersionService.create(shipmentTracking);
      },
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          history.replace(PRIVATE_ROUTES.SHIPMENTS_TRACKING);
        },
        onError: (error: AxiosError<Error>) => {
          if (error && error.response) {
            NotificationToast.error(translate("anErrorOccurredTryAgain"));
          }
        },
      }
    );
  };

  const useSendShareLink = (closeModal: (value: boolean) => void) => {
    return useMutation(
      ["shipment.tracking.share.link"],
      ({ shipmentId, emails }: ShareLinkProps) =>
        ShipmentTrackingVersionService.sendShareLink({
          shipmentId: Number(shipmentId),
          emails,
        }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.send.share.link",
          });
          closeModal(false);
        },
        onError: (err: AxiosError<Error>) => {
          if (err && err.response) NotificationToast.error(err.response.data.message);
        },
      }
    );
  };

  return {
    getAllShipmentTracking,
    getShipmentTrackingCount,
    getShipmentTrackingById,
    useCreateShipmentTracking,
    useUpdateShipmentTracking,
    useSendShareLink,
  };
};

export default useActionShipmentTracking;
